import { Alert, Box, Chip, Divider, Typography } from '@mui/material'
import CardActions from '@mui/material/CardActions'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import Collapse from '@mui/material/Collapse'
import Card from '@mui/material/Card'
import { forwardRef, ReactNode, useImperativeHandle, useState } from 'react'
import { styled } from '@mui/material/styles'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { TextField } from '@common/components/Form/TextField'
import { SPACING } from '@common/theme/spacing'

const ExpandMore = styled('div')(({ theme }) => ({
  display: 'flex',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}))

export type GroupedFilesBlockProps = {
  title: string
  subtitle: string
  children: ReactNode
  status?: 'PASSED' | 'FAILED'
  actions: ReactNode
  onCommentAdd?: (comment: string) => void
  showFileTypeError?: boolean
  showModalityError?: boolean
  showModalityMissing?: boolean
}

export type GroupedFilesBlockHandle = {
  validateComment: (forContinue?: boolean) => Promise<boolean>
}

const CommentSchema = z.object({
  comment: z.string().min(1, { message: 'Briefly clarify why this issue occurred' }),
})

type CommentParams = z.infer<typeof CommentSchema>

const GroupedFilesBlock = forwardRef<GroupedFilesBlockHandle, GroupedFilesBlockProps>(function GroupedFilesBlock(
  {
    title,
    subtitle,
    children,
    status,
    actions,
    onCommentAdd,
    showFileTypeError,
    showModalityError,
    showModalityMissing,
  },
  ref,
) {
  const [expanded, setExpanded] = useState(true)
  const { control, trigger, getValues, setError } = useForm<CommentParams>({
    mode: 'onChange',
    defaultValues: { comment: '' },
    resolver: zodResolver(CommentSchema),
  })

  useImperativeHandle(ref, () => ({
    validateComment: async (forContinue = false) => {
      setExpanded(true)
      const isValid = await trigger('comment')
      if (!isValid && forContinue) {
        // Append actionable hint when validation happens as part of Continue flow
        setError('comment', {
          type: 'manual',
          message: 'Briefly clarify why this issue occurred to continue',
        })
      }
      if (isValid) {
        onCommentAdd?.(getValues('comment'))
      }
      return isValid
    },
  }))

  return (
    <Card sx={{ p: SPACING.spacingXl, borderRadius: SPACING.borderRadiusXxl }} elevation={1}>
      {showFileTypeError && (
        <Alert severity="warning" sx={{ mb: SPACING.spacingLg }}>
          <Typography variant="body1" fontWeight="700" component={'p'}>
            Exam contains unexpected file type
          </Typography>
          <Typography variant="body1" component={'p'}>
            It isn’t required. Please remove it and add the correct file to complete the upload.
          </Typography>
        </Alert>
      )}
      {showModalityError && (
        <Alert severity="warning" sx={{ mb: SPACING.spacingLg }}>
          <Typography variant="body1" fontWeight="700" component={'p'}>
            Exam contains unexpected modality
          </Typography>
          <Typography variant="body1" component={'p'}>
            It isn’t required. Please remove it and add the correct file to complete the upload.
          </Typography>
        </Alert>
      )}

      {showModalityMissing && (
        <Alert sx={{ mb: 2 }} severity="warning">
          <Typography variant="body1" fontWeight="700" component={'p'}>
            Exam contains CT modality previously marked as missing
          </Typography>
          <Typography variant="body1" component={'p'}>
            You included a CT file after indicating you had none. Remove the CT file or adjust your container settings.
          </Typography>
        </Alert>
      )}
      <CardActions
        disableSpacing
        sx={{ p: SPACING.noSpacing, cursor: 'pointer', justifyContent: 'space-between', alignItems: 'center' }}
        onClick={() => setExpanded(!expanded)}
        aria-label="Expand or collapse"
      >
        <Box>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          {status && status === 'PASSED' ? (
            <Chip
              icon={<CheckCircleRoundedIcon color="success" />}
              label="Passed"
              variant="filled"
              color="success-alt"
              sx={{ mr: SPACING.spacingXl, borderRadius: 100 }}
            />
          ) : null}
          {status && status === 'FAILED' ? (
            <Chip
              icon={<ErrorRoundedIcon color="error" />}
              label="Failed"
              variant="filled"
              color="error-alt"
              sx={{ mr: SPACING.spacingXl, borderRadius: 100 }}
            />
          ) : null}
          {actions}
          <Divider orientation="vertical" flexItem sx={{ mx: SPACING.minSpacing }} variant="middle" />
          <ExpandMore
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <KeyboardArrowDownRoundedIcon />
          </ExpandMore>
        </Box>
      </CardActions>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {status === 'FAILED' ? (
          <Box mt={SPACING.spacingXxl}>
            <Box p={SPACING.spacingLg} bgcolor="paper.main" borderRadius={SPACING.borderRadiusXl}>
              <TextField
                name="comment"
                control={control}
                label="Comment"
                multiline
                rows={3}
                fullWidth
                helperText="Briefly clarify why this issue occurred"
                sx={{ mb: SPACING.minSpacing }}
                size="small"
              />
            </Box>
          </Box>
        ) : null}
        {children}
      </Collapse>
    </Card>
  )
})

export default GroupedFilesBlock
